import { normalizeLegacyAppFormsTreeId } from 'app/services/autoLockForms/autoLockForms.utils';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { selectCompletenessChecklistIdSet } from './completenessChecklists.selectors';
import { selectLoanApplicationChecklistResultsList } from './loanApplicationChecklistResults.selectors';

/**
 * Derives a map of the failing/incomplete checklist items that are linked to a
 * SECTION/area via `fieldRef` (DEV-4467 — the section-level sibling of
 * `selectIncompleteLinkedFields`).
 *
 * A section `fieldRef` is the FULL, prefix-discriminated area id emitted by the
 * appFormsTree (`subsection:financialInfo-employmentInfo`, `section:financialInfo`,
 * `tab:...`, `page:...`). Unlike the field selector — which scopes to a form and
 * keys by the bare leaf (`fieldRef.includes(formName)` + `split('-').pop()`) — a
 * section is matched by EXACT equality, so this map is keyed by the full ref and
 * `SectionPresenceWrapper` looks it up by its own area id directly.
 *
 * Keeping the map keyed by the raw ref (rather than filtering to prefixed refs)
 * is intentional: a field-level ref is prefix-less and can never equal a prefixed
 * area id, so a section lookup only ever resolves section-linked items.
 *
 * Value: `{ checklistId, itemId, isOverridden }`.
 *
 * Design decisions (mirrors `incompleteLinkedFields.selectors`):
 *  - Includes ALL linked items whose result is not `true` (failing or not yet evaluated).
 *  - Marks `isOverridden` when the item has been manually overridden.
 *  - When two checklists share the same area id, a non-overridden failing entry
 *    takes priority over an overridden one.
 *
 * No borrower-dimension keying needed here (DEV-4684, investigated): `SectionPresenceWrapper`
 * only ever looks up a PREFIXED area ref (`subsection:`/`section:`/`tab:`/`page:`); a
 * Borrower-scoped snapshot's item carries a prefix-less FIELD `fieldRef`, which can never equal
 * a prefixed area id, so it is never resolved through this map — no per-borrower collision is
 * observable here, unlike `selectIncompleteLinkedFields`.
 *
 * @returns selector → `{ [areaId: string]: { checklistId: string, itemId: string, isOverridden: boolean } }`
 */
export const selectIncompleteLinkedSections = memoizeOne(() =>
  createSelector(
    selectLoanApplicationChecklistResultsList,
    selectCompletenessChecklistIdSet,
    (checklistResultsList, completenessChecklistIdSet) => {
      const map = {};

      checklistResultsList.forEach(snapshot => {
        const checklistId = snapshot.target?.checklistId;
        const items = snapshot.status?.result?.items;

        // Only the company's completeness checklist(s) drive the section-level highlighting (DEV-4603).
        if (!checklistId || !items || !completenessChecklistIdSet.has(checklistId)) {
          return;
        }

        items.forEach(item => {
          const { checklistItemId, result, fieldRef, overrideHistory } = item;

          if (!fieldRef || result === true) {
            return;
          }

          const isOverridden = !!overrideHistory?.wasOverriden;
          // Links saved before LEN2-499 may carry the nameless Lead/Activities/Tasks ids.
          const areaRef = normalizeLegacyAppFormsTreeId(fieldRef);
          const existing = map[areaRef];

          // Non-overridden entries take priority over overridden ones for the same area.
          if (existing && !existing.isOverridden && isOverridden) {
            return;
          }

          map[areaRef] = { checklistId, itemId: checklistItemId, isOverridden };
        });
      });

      return map;
    },
  ),
);
